import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { setup_product_accounting_config, update_product_accounting_config } from "services/apiSrv";
import { show_toast } from "utils/helpers";
import { useParams } from "react-router-dom";

const validationSchema = Yup.object().shape({
  productAccountingConfigName: Yup.string().required("Product Accounting Config Name is required"),
  handleTaxes: Yup.string().required("This field is required").oneOf(['yes', 'no']),
  generateEtims: Yup.string().when("handleTaxes", {
    is: "yes",
    then: (schema) => schema.required("This field is required").oneOf(['yes', 'no']),
  }),
  remitTax: Yup.string().when("handleTaxes", {
    is: "yes",
    then: (schema) => schema.required("This field is required").oneOf(['yes', 'no']),
  }),
  etimsTaxNumber: Yup.string().when("handleTaxes", {
    is: "yes",
    then: (schema) => schema.required("ETIMS Tax Number is required"),
  }),
  etimsToken: Yup.string().when("handleTaxes", {
    is: "yes",
    then: (schema) => schema.required("ETIMS Token is required"),
  }),
  offerCommission: Yup.string().required("This field is required").oneOf(['yes', 'no']),
  commissionRate: Yup.number().when("offerCommission", {
    is: "yes",
    then: (schema) => schema.required("Commission rate is required").positive().max(100),
  }),
  paymentMethod: Yup.string().when("offerCommission", {
    is: "yes",
    then: (schema) => schema.required("Payment method is required").oneOf(['auto', 'manual']),
  }),
});

const defaultInitialValues = {
  productAccountingConfigName: "",
  handleTaxes: "",
  generateEtims: "",
  remitTax: "",
  etimsTaxNumber: "",
  etimsToken: "",
  offerCommission: "",
  commissionRate: "",
  paymentMethod: "manual",
};

export default function AccountingConfigForm({ refetchProductDetails, initialConfig }) {
  const { id: productSystemRef } = useParams();
  console.log("initialconfig==", initialConfig);

  // Merge initialConfig with default values
  const initialValues = {
    ...defaultInitialValues,
    ...(initialConfig || {}),
    // Convert numeric fields to strings for form compatibility
    commissionRate: initialConfig?.commissionRate ? String(initialConfig.commissionRate) : "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Prepare submission data
      const submissionData = {
        ...values,
        productSystemRef,
        commissionRate: values.commissionRate ? Number(values.commissionRate) : undefined,
      };

      if (initialConfig?.id) {
        // Update existing configuration
        await update_product_accounting_config({ ...submissionData, configId: initialConfig.id });
        show_toast("Accounting Configuration updated successfully!", "success");
      } else {
        // Create new configuration
        await setup_product_accounting_config(submissionData);
        show_toast("Accounting Configuration saved successfully!", "success");
      }

      refetchProductDetails?.();
      if (!initialConfig) resetForm();
    } catch (error) {
      show_toast(
        `Error ${initialConfig ? "updating" : "saving"} accounting configuration: ${error.message}`,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: "24px", background: "#fff", borderRadius: "8px", boxShadow: "0 0 8px rgb(0 0 0 / 0.1)", margin: "24px 0" }}>
      <h5 style={{ marginBottom: "24px", fontWeight: 600, color: "#333" }}>
        {initialConfig ? "Edit Accounting Configuration" : "Create Accounting Configuration"}
      </h5>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values }) => (
          <Form>
            {/* General Configuration */}
            <div style={{ marginBottom: "48px" }}>
              <div style={{ background: "#f4f4f4", padding: "16px", borderRadius: "8px", marginBottom: "16px" }}>
                <h6 style={{ margin: 0, fontWeight: 600, fontSize: "16px", color: "#555" }}>General Configuration</h6>
              </div>
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontWeight: 600, color: "#555", marginBottom: "8px" }}>
                  Product Accounting Config Name
                </label>
                <Field
                  name="productAccountingConfigName"
                  placeholder="Enter Product Accounting Config Name"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px"
                  }}
                />
                <ErrorMessage
                  name="productAccountingConfigName"
                  component="div"
                  style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
                />
              </div>
            </div>

            {/* Tax Configuration */}
            <div style={{ marginBottom: "48px" }}>
              <div style={{ background: "#f4f4f4", padding: "16px", borderRadius: "8px", marginBottom: "16px" }}>
                <h6 style={{ margin: 0, fontWeight: 600, fontSize: "16px", color: "#555" }}>Tax Configuration</h6>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", marginBottom: "24px", gap: "2rem" }}>
                <label style={{ fontWeight: 600, color: "#555", margin: 0, minWidth: "300px" }}>
                  Do you want us to handle taxes on your behalf?
                </label>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Field type="radio" name="handleTaxes" value="yes" style={{ margin: 0 }} />
                    <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>Yes</label>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Field type="radio" name="handleTaxes" value="no" style={{ margin: 0 }} />
                    <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>No</label>
                  </div>
                </div>
              </div>
              <ErrorMessage name="handleTaxes" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />

              {values.handleTaxes === "yes" && (
                <div style={{ paddingLeft: "24px", borderLeft: "2px solid #ddd", marginTop: "16px" }}>
                  {/* Generate ETIMS */}
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "24px", gap: "2rem" }}>
                    <label style={{ fontWeight: 600, color: "#555", margin: 0, minWidth: "300px" }}>
                      Generate ETIMS compliant certificate?
                    </label>
                    <div style={{ display: "flex", gap: "1rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Field type="radio" name="generateEtims" value="yes" style={{ margin: 0 }} />
                        <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>Yes</label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Field type="radio" name="generateEtims" value="no" style={{ margin: 0 }} />
                        <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>No</label>
                      </div>
                    </div>
                  </div>
                  <ErrorMessage name="generateEtims" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />

                  {/* Remit Tax */}
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "24px", gap: "2rem" }}>
                    <label style={{ fontWeight: 600, color: "#555", margin: 0, minWidth: "300px" }}>
                      Remit tax?
                    </label>
                    <div style={{ display: "flex", gap: "1rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Field type="radio" name="remitTax" value="yes" style={{ margin: 0 }} />
                        <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>Yes</label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Field type="radio" name="remitTax" value="no" style={{ margin: 0 }} />
                        <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>No</label>
                      </div>
                    </div>
                  </div>
                  <ErrorMessage name="remitTax" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />

                  {/* ETIMS Config */}
                  <div style={{ marginBottom: "24px" }}>
                    <h6 style={{ fontWeight: 600, color: "#555", marginBottom: "16px" }}>
                      ETIMS Config
                    </h6>
                    <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: "block", fontWeight: 600, color: "#555", marginBottom: "8px" }}>
                          ETIMS Tax Number
                        </label>
                        <Field
                          name="etimsTaxNumber"
                          placeholder="Enter ETIMS Tax Number"
                          style={{
                            width: "100%",
                            padding: "12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px"
                          }}
                        />
                        <ErrorMessage name="etimsTaxNumber" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <label style={{ display: "block", fontWeight: 600, color: "#555", marginBottom: "8px" }}>
                          ETIMS Token
                        </label>
                        <Field
                          name="etimsToken"
                          placeholder="Enter ETIMS Token"
                          style={{
                            width: "100%",
                            padding: "12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "14px"
                          }}
                        />
                        <ErrorMessage name="etimsToken" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Commission Configuration */}
            <div style={{ marginBottom: "48px" }}>
              <div style={{ background: "#f4f4f4", padding: "16px", borderRadius: "8px", marginBottom: "16px" }}>
                <h6 style={{ margin: 0, fontWeight: 600, fontSize: "16px", color: "#555" }}>Commission Configuration</h6>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", marginBottom: "24px", gap: "2rem" }}>
                <label style={{ fontWeight: 600, color: "#555", margin: 0, minWidth: "300px" }}>
                  Do you offer commissions to your Salesrep?
                </label>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Field type="radio" name="offerCommission" value="yes" style={{ margin: 0 }} />
                    <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>Yes</label>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Field type="radio" name="offerCommission" value="no" style={{ margin: 0 }} />
                    <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>No</label>
                  </div>
                </div>
              </div>
              <ErrorMessage name="offerCommission" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />

              {values.offerCommission === "yes" && (
                <div style={{ paddingLeft: "24px", borderLeft: "2px solid #ddd", marginTop: "16px" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <label style={{ display: "block", fontWeight: 600, color: "#555", marginBottom: "8px" }}>
                      Commission Rate (%)
                    </label>
                    <Field
                      name="commissionRate"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Enter commission rate"
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px"
                      }}
                    />
                    <ErrorMessage name="commissionRate" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", marginBottom: "24px", gap: "2rem" }}>
                    <label style={{ fontWeight: 600, color: "#555", margin: 0, minWidth: "200px" }}>
                      Payment Method
                    </label>
                    <div style={{ display: "flex", gap: "1rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Field type="radio" name="paymentMethod" value="auto" style={{ margin: 0 }} />
                        <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>Auto</label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Field type="radio" name="paymentMethod" value="manual" style={{ margin: 0 }} />
                        <label style={{ margin: 0, fontWeight: 500, color: "#555", cursor: "pointer" }}>Manual</label>
                      </div>
                    </div>
                  </div>
                  <ErrorMessage name="paymentMethod" component="div" style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }} />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="submit"
                style={{
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  padding: "12px 32px",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  opacity: isSubmitting ? 0.6 : 1
                }}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : initialConfig
                  ? "Update Accounting Configuration"
                  : "Save Accounting Configuration"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}